import React from "react";
import { ICellRendererParams } from "ag-grid-community";
import { Flex, Link, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors, Sizes } from "@jsq/ds/styles";
import { globalEnv } from "~/global-env";
import { ComplianceCaseTypeEnum } from "~/graphql/supergraphEnums";
import { type ScreeningRecordResult } from "~/graphql/supergraphTypes";
import { screeningStatusToIconMap } from "~/pages/Compliance/CaseDetailsDrawer/Screening/ScreeningStatusRenderer/constants";
import { ScreeningStatusCell } from "~/pages/Compliance/CaseDetailsDrawer/Screening/ScreeningStatusRenderer/RiskRatingCell";
import { gridCellStyles } from "~/pages/Compliance/Common/GridCellBackgrounds/styles_vanilla.css";
import { SCREENING_STATUS_CONFIG } from "./config";
import { linkContainer, screeningCellContainer, textColorVariant } from "./styles_vanilla.css";

const ScreeningStatusDisplay: React.FC<{
  status: NonNullable<ScreeningRecordResult>;
  to: string;
}> = ({ status, to }) => {
  const config = SCREENING_STATUS_CONFIG[status];

  return (
    <Flex alignItems="center" justifyContent="space-between" className={screeningCellContainer}>
      <Flex alignItems="center" gap={Sizes.SM}>
        <Icon type={config.icon.type} variant="outline" color={config.icon.color} />
        <Text variant="strong-md" className={config.text.variant}>
          {config.text.content}
        </Text>
      </Flex>
      <Link
        iconPosition="right"
        icon="arrow-right"
        color={Colors.Primary600}
        to={to}
        className={linkContainer}
      >
        {config.linkText}
      </Link>
    </Flex>
  );
};

export const ScreeningCellRenderer: React.FC<ICellRendererParams> = ({
  data,
  value,
  context,
}) => {
  if (value?.screeningRecord) {
    const { caseId, profileId } = value;
    const caseType = context?.caseType;
    const isPicr = caseType === ComplianceCaseTypeEnum.PaymentInstructionChangeReview;
    const route = isPicr ? "positions-screening-details" : "screening-details";
    const to = `${globalEnv.complianceRoute}/case/${caseId}/${route}?profileId=${profileId}`;
    return <ScreeningStatusDisplay status={value.screeningRecord} to={to} />;
  }

  if (!data?.paymentBeneficiary) {
    return <Text className={textColorVariant.neutral}>Select payment beneficiary</Text>;
  }

  const notScreenedConfig = screeningStatusToIconMap.NotScreened;
  return (
    <ScreeningStatusCell
      hasIcon={true}
      iconType={notScreenedConfig.icon}
      color={notScreenedConfig.color}
      text={notScreenedConfig.text}
    />
  );
};

export const getScreeningCellStyle = (
  screening: ScreeningRecordResult | null | undefined,
): string => {
  const styleMap: Record<NonNullable<string>, string> = {
    NoMatches: gridCellStyles.neutral,
    PotentialMatches: gridCellStyles.warning,
    FalsePositive: gridCellStyles.neutral,
    TrueMatch: gridCellStyles.danger,
  };

  return screening ? styleMap[screening] : gridCellStyles.default;
};
